<template>
  <div>
    <input type="hidden" :name="name" :value="html"/>
    <ckeditor
      ref="ckeditor"
      v-model="html"
      :editor="editor"
      :config="editorConfig"
      :disabled="disabled"
      v-if="!html_mode"
      @ready="$emit('ready')"
    />
    <b-form-group v-if="html_mode">
      <b-form-textarea
        v-model="html"
        placeholder="HTML"
        rows="6"
        ref="html-textarea"
      ></b-form-textarea>
    </b-form-group>
    <b-row class="mt-1">
      <b-col class="col-auto">
        <b-button variant="link" class="text-dark p-0" @click="onRobotClick" v-b-tooltip.hover :title="trans('try_ai')">
          <i class="fa fa-fw fa-robot"></i>
          <small class="d-none d-lg-inline">{{ trans('ai_robot') }}</small>
        </b-button>
        <b-form-checkbox switch v-model="html_mode" v-if="showHtmlMode">
          {{ trans('html_mode') }}
        </b-form-checkbox>
      </b-col>
      <b-col v-if="!hideTips" class="col text-right text-muted">
        <small v-html="trans('html_tips')"></small>
      </b-col>
    </b-row>
    <insert-form-document-link-modal
      v-if="organization && showInsertFormDocument"
      :organization="organization"
    ></insert-form-document-link-modal>
    <insert-placeholder-modal
      v-if="organization && hasGroupedPlaceholders"
      :organization="organization"
      :placeholders="placeholders"
    ></insert-placeholder-modal>
    <chat-gpt-modal
      :organization="organization"
      :text="html"
      ref="chat-gpt-modal"
      @input="onAiInput"
      @replace="html = $event"
    ></chat-gpt-modal>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline'
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough'
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import Font from '@ckeditor/ckeditor5-font/src/font'
import Link from '@ckeditor/ckeditor5-link/src/link'
import List from '@ckeditor/ckeditor5-list/src/list'
import Indent from '@ckeditor/ckeditor5-indent/src/indent'
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock'
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import Placeholder from '@ckeditor/ckeditor5-placeholder/src/placeholder'
import SimpleButton from '@ckeditor/ckeditor5-simple-button/src/simple-button-plugin'
import AutoImage from '@ckeditor/ckeditor5-image/src/autoimage'
import Image from '@ckeditor/ckeditor5-image/src/image'
import InsertFormDocumentLinkModal from './../../frontend/components/template/InsertFormDocumentLinkModal'
import InsertPlaceholderModal from './InsertPlaceholderModal'
import ChatGptModal from './ChatGptModal'
import CKEditor from '@ckeditor/ckeditor5-vue2'

export default {
  props: {
    organization: Object,
    name: String,
    value: [String, Boolean],
    disabled: {
      type: Boolean,
      default: false,
    },
    wordLimit: Number | String,
    showInsertFormDocument: {
      type: Boolean,
      default: false,
    },
    showHtmlMode: {
      type: Boolean,
      default: false,
    },
    forMicrosite: {
      type: Boolean,
      default: false,
    },
    html_mode_value: {
      type: Boolean,
      default: false,
    },
    placeholders: Array | Object,
    // Array = simple list, Object = nested "grouped" list
    // When a list, use a dropdown, when an object, use a modal
    // Omit if no placeholders
    hideHighlightHeading: {
      type: Boolean,
      default: false,
    },
    hideTips: {
      type: Boolean,
      default: false,
    },
    allowImages: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SimpleButton,
    InsertFormDocumentLinkModal,
    InsertPlaceholderModal,
    ChatGptModal,
    ckeditor: CKEditor.component,
  },
  data() {
    return {
      trans: trans,
      lang: lang,
      wordCount: 0,
      word_limit: parseInt(this.wordLimit),
      html: this.value || '',
      editor: ClassicEditor,
      html_mode: this.html_mode_value || false,
    }
  },
  computed: {
    hasListPlaceholders() {
      return !!this.placeholders && _.isArray(this.placeholders)
    },
    hasGroupedPlaceholders() {
      return !this.hasListPlaceholders && _.isObject(this.placeholders)
    },
    editorConfig() {
      return {
        language: lang,
        plugins: _.compact([
          Heading,
          Essentials,
          Bold,
          Italic,
          Underline,
          Strikethrough,
          Superscript,
          Font,
          Link,
          List,
          Indent,
          IndentBlock,
          Alignment,
          BlockQuote,
          Placeholder,
          SimpleButton,  
          this.allowImages ? Image : null,
          this.allowImages ? AutoImage : null,
        ]),
        toolbar: _.compact([
          'heading',
          this.organization && this.showInsertFormDocument ? 'insertFormDocumentLink' : null,
          this.hasGroupedPlaceholders ? 'insertPlaceholder' : null,
          this.hasListPlaceholders ? 'placeholder' : null,
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'superscript',
          '|',
          'fontFamily',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          'link',
          'blockQuote',
          '|',
          'alignment',
          'bulletedList',
          'numberedList',
          'indent',
          'outdent',
          '|',
          'undo',
          'redo',
        ]),
        removePlugins: ['contextmenu', 'liststyle', 'tabletools', 'tableselection'],
        heading: {
          options: _.compact([
            {model: 'paragraph', title: trans('paragraph'), class: 'ck-heading_paragraph'},
            {model: 'heading1', view: 'h1', title: `${trans('heading')} 1`, class: 'ck-heading_heading1'},
            {model: 'heading2', view: 'h2', title: `${trans('heading')} 2`, class: 'ck-heading_heading2'},
            {model: 'heading3', view: 'h3', title: `${trans('heading')} 3`, class: 'ck-heading_heading3'},
            this.forMicrosite ? {
              model: 'heading4',
              view: 'h4',
              title: `${trans('heading')} 4`,
              class: 'ck-heading_heading4'
            } : null,
            this.forMicrosite ? {
              model: 'heading5',
              view: 'h5',
              title: `${trans('heading')} 5`,
              class: 'ck-heading_heading5'
            } : null,
            this.hideHighlightHeading ? null : {
              model: 'highlight_branded',
              view: {name: 'p', classes: 'highlight-branded'},
              title: trans('highlight_branded'),
              class: 'ck-heading_highlight-branded',
              converterPriority: 'high'
            },
            this.forMicrosite || this.hideHighlightHeading ? null : {
              model: 'highlight',
              view: {name: 'p', classes: 'highlight'},
              title: trans('highlight'),
              class: 'ck-heading_highlight',
              converterPriority: 'high'
            },
          ]),
        },
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'http://',
        },
        fontSize: _.ckeditorFontSize,
        fontColor: {
          colors: _.ckeditorColors(this.organization),
          columns: 5
        },
        placeholderProps: this.hasListPlaceholders ? {
          types: this.placeholders,
        } : null,
        simpleButton: _.compact([
          InsertFormDocumentLinkModal.buildSimpleButton(this),
          this.hasGroupedPlaceholders ? InsertPlaceholderModal.buildSimpleButton(this) : null,
        ]),
      }
    },
  },
  watch: {
    html() {
      this.$emit('input', this.html || null)
    },
    value() {
      this.html = this.value || ''
    },
    html_mode() {
      if (this.html_mode) {
        this.$nextTick(() => {
          this.$refs['html-textarea'].focus()
        })
      }
      this.$emit('html', this.html_mode)
    },
    html_mode_value() {
      this.html_mode = this.html_mode_value
    },
  },
  methods: {
    onRobotClick() {
      this.$refs['chat-gpt-modal'].$refs['the-modal'].show()
    },
    onAiInput(e) {
      this.html += e
    },
  },
  mounted() {
    window.htmlTextarea = this
  },
}
</script>
